/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useForm } from "react-hook-form";
import { Button, Description, Input, Label, Sidebar } from "@nordcloud/gnui";
import {
  useAddOrgUnitTypeMutation,
  AddOrgUnitTypeV2,
} from "~/generated/graphql";
import { Fieldset } from "~/components";
import { showSuccess } from "~/services/toast";
import { generateActionSuccessText, handleError, toInputError } from "~/tools";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (id: string) => void;
};

export function AddOrgUnitTypeInlineForm({ isOpen, onClose, onAdd }: Props) {
  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm();
  const [addOrgUnitType] = useAddOrgUnitTypeMutation();

  const onAddOrgUnitType = async (values: AddOrgUnitTypeV2) => {
    try {
      const newOrgUnitType = await addOrgUnitType({
        variables: { input: values },
      });

      const orgUnitTypeId = newOrgUnitType?.data?.addOrgUnitTypeV3?.id;
      showSuccess(generateActionSuccessText(`The ${values.name}`)()("added")());
      onAdd(orgUnitTypeId ?? "");
    } catch (error) {
      handleError(toInputError(error));
    }
  };

  return (
    <Sidebar
      title="New organization unit type"
      isOpen={isOpen}
      onClick={onClose}
    >
      <form
        // Workaround for issue with React 17 event handling and nested forms https://github.com/facebook/react/issues/20741#issuecomment-870776352
        onSubmitCapture={handleSubmit(onAddOrgUnitType)}
      >
        <Fieldset>
          <Label required name="Name" />
          <Input
            type="text"
            {...register("name", { required: true })}
            placeholder="Name"
            status={errors.name && "error"}
          />
          {errors.name && <Description>Name is required.</Description>}
        </Fieldset>
        <Fieldset>
          <Label name="Description" />
          <Input
            type="text"
            {...register("description")}
            placeholder="Description"
          />
        </Fieldset>
        <Button type="submit" icon="plus">
          Add new Unit Type
        </Button>
      </form>
    </Sidebar>
  );
}
